import { useMemo } from 'react';
import { FieldGroup } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { SchemaField } from './SchemaField';
import type { SchemaFormProps, SchemaWidget } from './SchemaForm.types';
import { getUiSchemaFor, pathToId } from './schemaUtils';
import { type SchemaValidationMessages, validateFormData } from './schemaValidation';

const NO_WIDGETS: Record<string, SchemaWidget> = {};

/**
 * Renders a form from a JSON Schema supplied at runtime. It is fully controlled:
 * every edit reports the whole form data back, and validation runs on each render
 * so messages track the current values without a separate submit step.
 *
 * The supported subset is objects of scalars, enums, arrays, records
 * (`additionalProperties`) and nested objects. A property using anything else
 * renders as an unsupported field rather than disappearing.
 */
export function SchemaForm({
  schema,
  uiSchema,
  formData,
  onChange,
  disabled = false,
  widgets = NO_WIDGETS,
  idPrefix = 'schema-form',
}: SchemaFormProps) {
  const { t } = useTranslation('common');

  const messages = useMemo<SchemaValidationMessages>(
    () => ({
      required: t('ui.schemaForm.required'),
      invalidText: t('ui.schemaForm.invalidText'),
      invalidNumber: t('ui.schemaForm.invalidNumber'),
      invalidBoolean: t('ui.schemaForm.invalidBoolean'),
      notAnOption: t('ui.schemaForm.notAnOption'),
      tooFewItems: (min) => t('ui.schemaForm.tooFewItems', { count: min }),
      tooManyItems: (max) => t('ui.schemaForm.tooManyItems', { count: max }),
      tooShort: (min) => t('ui.schemaForm.tooShort', { count: min }),
      tooLong: (max) => t('ui.schemaForm.tooLong', { count: max }),
      tooSmall: (min) => t('ui.schemaForm.tooSmall', { min }),
      tooLarge: (max) => t('ui.schemaForm.tooLarge', { max }),
    }),
    [t],
  );

  const errors = useMemo(
    () => validateFormData(schema, formData, messages),
    [schema, formData, messages],
  );

  const properties = schema.properties;
  const keys = properties === undefined ? [] : Object.keys(properties);
  if (properties === undefined || keys.length === 0) return null;

  const requiredKeys = new Set(schema.required ?? []);

  return (
    <FieldGroup data-testid="schema-form">
      {keys.map((key) => (
        <SchemaField
          key={key}
          name={key}
          path={key}
          id={pathToId(idPrefix, key)}
          idPrefix={idPrefix}
          schema={properties[key]}
          uiSchema={getUiSchemaFor(uiSchema, key)}
          value={formData[key]}
          onChange={(next) => onChange({ ...formData, [key]: next })}
          required={requiredKeys.has(key)}
          disabled={disabled}
          widgets={widgets}
          errors={errors}
        />
      ))}
    </FieldGroup>
  );
}
